import { observer } from 'mobx-react';
import { FC, useMemo } from 'react';
import { checkFranchisePosAccess } from '../../helpers/franchiseManagement';
import { franchiseManagementStore } from '../../store/franchiseManagementStore';
import { BUTTON_TITLE } from '~/constants/main';
import integrationsStore from '~/mobx/integrationStore';
import { localeStore } from '~/mobx/localesStore';
import { TBooleanInNumber } from '~/types/main';
import { scrollToErrorElement } from '~/utils/helpers';
import { confirmMessage } from '~/utils/modalConfirmations';
import Button from '~/view/components/Controls/ButtonComponent/Button';
import { CurrencyPicker } from '~/view/components/Controls/Pickers/CurrencyPicker/CurrencyPicker';
import { TimeZonePicker } from '~/view/components/Controls/Pickers/TimeZonePicker/TimeZonePicker';
import Text, { TEXT } from '~/view/components/Text/Text';
import ScrollableBlock from '~/view/containers/ScrollableBlock/ScrollableBlock';
import { RegCertificateUpload } from '~/view/screens/SettingsScreen/screens/Orders/components/RegCertificateUplod/RegCertificateUpload';
import { VatRegNumber } from '~/view/screens/SettingsScreen/screens/Orders/components/VatRegNumber/VatRegNumber';
import { TaxConfiguration } from '~/view/screens/SettingsScreen/screens/Orders/containers/TaxConfiguration/TaxConfiguration';
import './style.scss';

export const FranchiseProfileSettings: FC = observer((): JSX.Element => {
  const { selectedFranchiseProfile, changeFranchiseProfileSettings } = franchiseManagementStore;

  const vatIncludedPosAccess = useMemo(
    () => checkFranchisePosAccess(selectedFranchiseProfile, integrationsStore.posRestrictions?.vatIncludedToggle),
    [selectedFranchiseProfile]
  );
  const currencyPosAccess = useMemo(
    () => checkFranchisePosAccess(selectedFranchiseProfile, integrationsStore.posRestrictions?.currency),
    [selectedFranchiseProfile]
  );

  const handleVayIncludedChange = (status: TBooleanInNumber) => {
    changeFranchiseProfileSettings({ vatIncluded: status });
  };
  const handleTaxableDeliveryFeeChange = (status: TBooleanInNumber) => {
    changeFranchiseProfileSettings({ taxableDeliveryFee: status });
  };
  const handleVatAmountChange = (value: string | undefined) => {
    changeFranchiseProfileSettings({ vat: value });
    if (value) franchiseManagementStore.validateVat();
  };

  const handleVatRegNumberChange = (value: string | undefined) => {
    changeFranchiseProfileSettings({ vatRegistrationNumber: value });
    if (value) franchiseManagementStore.validateVatRegNumber();
  };

  const handleCurrencySelect = (id: string, code: string) => {
    changeFranchiseProfileSettings({ currencyId: id, currencyCode: code });
    confirmMessage({
      title: localeStore.t('deactivate_branches'),
      message: localeStore.t('deactivate_branches_msg'),
      confirmBtnTxt: localeStore.t('deactivate'),
      cancelBtnTxt: localeStore.t(BUTTON_TITLE.CANCEL),
    });
  };

  const handleTimezoneSelect = (value: string) => {
    changeFranchiseProfileSettings({ timezone: value });
  };

  const handleCertificateUpload = (file: File) => {
    changeFranchiseProfileSettings({ certificate: file, certificateFilename: file.name, certificateUrl: '' });
    franchiseManagementStore.validateCertificateFile();
  };
  const handleCertificateUnlink = (file: File) => {
    franchiseManagementStore.changeFranchiseProfileSettings({ certificate: file, certificateFilename: file.name });
  };

  const handleSave = () => {
    franchiseManagementStore.validateBeforeSave();

    if (franchiseManagementStore.isValidationFailed) {
      scrollToErrorElement();
    } else {
      franchiseManagementStore.updateProfileData();
    }
  };

  return (
    <div className="d-flex flex-column">
      <ScrollableBlock className="franchise-profile-settings ml-4" scrollbarSize="small" showScrollbar>
        <div>
          <Text text="tax_amount" type={TEXT.SECTION_HEADER} />
          <TaxConfiguration
            vatIncluded={selectedFranchiseProfile.vatIncluded}
            taxableDeliveryFee={selectedFranchiseProfile.taxableDeliveryFee}
            vatAmount={selectedFranchiseProfile.vat}
            vatIncludedAccess={vatIncludedPosAccess}
            vatAccess={integrationsStore.posRestrictions?.amountPercent}
            vatAmountError={franchiseManagementStore.errorMessages.vat}
            onChangeVatIncluded={handleVayIncludedChange}
            onChangeTaxableDeliveryFee={handleTaxableDeliveryFeeChange}
            onChangeVatAmount={handleVatAmountChange}
          />
        </div>
        <div className="mt-6">
          <VatRegNumber
            value={selectedFranchiseProfile.vatRegistrationNumber}
            error={franchiseManagementStore.errorMessages.vatRegNumber}
            onChange={handleVatRegNumberChange}
          />
        </div>
        <div className="d-flex flex-column gap-15 mt-3">
          <CurrencyPicker
            id={selectedFranchiseProfile.currencyId}
            code={selectedFranchiseProfile.currencyCode}
            access={currencyPosAccess}
            onSelect={handleCurrencySelect}
          />
          <TimeZonePicker timezone={selectedFranchiseProfile.timezone} onSelect={handleTimezoneSelect} />
        </div>
        <RegCertificateUpload
          name={selectedFranchiseProfile.certificateFilename}
          link={selectedFranchiseProfile.certificateUrl}
          error={franchiseManagementStore.errorMessages.certificate}
          onUpload={handleCertificateUpload}
          onUnlink={handleCertificateUnlink}
        />
      </ScrollableBlock>
      <Button title={localeStore.t(BUTTON_TITLE.SAVE)} onClick={handleSave} className="mt-6 ml-4" />
    </div>
  );
});
